/**
 * common — the English half, typed against the German module, so a
 * missing or misspelled key is a type error in this file.
 */
import type { common as de_common } from '../de/common';

export const common: Record<keyof typeof de_common, string> = {
  /* ---------------- app chrome ---------------- */
  'app.title': 'Aether · VFR flight planning',
  'app.description': 'VFR flight planning for Europe: map, nav log, weather, terrain — and the German LBA form.',
  'app.notfound': 'This page does not exist.',
  'app.toflights': 'Go to flights',
  'footer.sources': 'Data sources & licences',
  loading: 'Loading …',
  locale: 'en-GB',
  yes: 'yes',
  no: 'no',

  /* ---------------- warnings (by code) ---------------- */
  'warn.route-incomplete':
    'At least one leg has a distance but no time — total time and ETA are incomplete.',
  'warn.eta-next-day': 'ETA is on the next day — long after sunset!',
  'warn.eta-after-sunset': 'ETA is after sunset!',
  'warn.eta-near-sunset': 'ETA is less than 30 min before sunset.',
  'warn.exceeds-safe-time': 'Total flight time exceeds the safe flight time!',
  'warn.leg-unflyable': 'Leg {leg}: wind too strong for the entered TAS — no computation.',
  'warn.wind-ge-tas': 'Wind component ≥ TAS — leg cannot be flown as computed',

  /* ---------------- field validation ---------------- */
  'invalid.time': 'Invalid time — HH:MM (UTC) expected, e.g. 11:30.',
  'invalid.hhmm': 'Invalid time — four digits expected, e.g. 1120.',
  'invalid.date': 'Invalid date — DD.MM.YYYY expected, e.g. 17.8.2026.',
  'invalid.number': 'Not a valid number.',
  'invalid.heading': 'Invalid course — 0 to 360 degrees expected.',
  'invalid.wind': 'Invalid wind — DDD/SS expected, e.g. 270/10 or 270/10G20.',
  'invalid.wca': 'Invalid wind correction angle — e.g. 5+ or 3-.',

  /* ---------------- API / IO errors (by code) ---------------- */
  'err.network': 'No connection to the server.',
  'err.unauthenticated': 'Please sign in.',
  'err.forbidden': 'Not permitted.',
  'err.not_found': 'Not found.',
  'err.bad_credentials': 'Email or password is not correct.',
  'err.conflict': 'That entry already exists.',
  'err.version_conflict': 'The plan has meanwhile been changed elsewhere.',
  'err.invalid': 'Input is not valid.',
  'err.invalid_token': 'This link is invalid or has expired.',
  'err.internal': 'Server error.',
  'err.unavailable': 'The service is not available right now.',
  'err.http': 'Request failed (HTTP {status}).',
  'err.pdf': 'The PDF could not be created: {error}',
  'err.file': 'The file could not be read: {error}',
  'err.share': 'Sharing failed: {error}',
  'err.notAnObject': 'not an object',

  /* ---------------- how fresh the data is (autoload.ts) ---------------- */
  'fresh.fam.wx': 'Weather',
  'fresh.fam.winds': 'Wind',
  'fresh.fam.terrain': 'Terrain',
  'fresh.fam.geo': 'Aerodrome data',
  'fresh.at': 'updated {time}',
  'fresh.ago': '{n} min ago',
  'fresh.now': 'just updated',
  'fresh.busy': 'updating',
  'fresh.refresh': 'Refresh',
  'fresh.info': 'Where does this come from?',
  'fresh.obs': 'METAR {time}',
  'fresh.err.wx': 'Weather is not reachable right now – retrying.',
  'fresh.err.winds': 'Wind is not reachable right now – retrying.',
  'fresh.err.terrain': 'Terrain is not reachable right now – retrying.',
  'fresh.err.geo': 'Aerodrome data is not reachable right now – retrying.',
  'src.info.wx':
    'METAR and TAF for the aerodromes in the plan. They come from the US National Weather Service (NOAA) and are re-read every ten minutes. Does not replace an official weather briefing.',
  'src.info.winds':
    'Wind forecast for the middle of each leg, at the planned altitude and time. It comes from Open-Meteo, based on the models of the German weather service. Planning aid, not a weather briefing.',
  'src.info.terrain':
    'Highest terrain and tallest obstacle in the corridor along the route. Terrain elevations by Mapterhorn, obstacles by openAIP. Some obstacles are published without a height. Advisory, not a clearance.',
  'src.info.geo':
    'Aerodromes, runways, frequencies, navaids, reporting points and airspace from the open aeronautical database openAIP. They arrive as soon as the route is drawn. Not official – check AIP and NOTAM before the flight.',
  'src.info.airspace':
    'Airspace along the route, checked at the planned altitude. From the open aeronautical database openAIP. Advisory, not a clearance.',
  'src.more': 'All data sources & licences',
  'src.page.title': 'Data sources & licences',
  'src.page.lead':
    'Aether shows data from public services. This page says which ones and under what licence. None of them replaces official flight preparation: AIP, NOTAM and the weather briefing remain binding.',
  'src.page.wx.title': 'Weather – METAR and TAF',
  'src.page.wx.body':
    'Aerodrome weather reports and forecasts from the NOAA Aviation Weather Center (US National Weather Service). A work of the US federal government and therefore in the public domain; no attribution is required. Official briefing in Germany: Deutscher Wetterdienst, aviation.',
  'src.page.winds.title': 'Wind and temperature aloft',
  'src.page.winds.body':
    'Weather data by Open-Meteo.com, licensed under CC BY 4.0. Based on the ICON models of the German weather service (DWD), also CC BY 4.0. Forecast data – a planning aid, not a weather briefing.',
  'src.page.geo.title': 'Aerodrome data, airspace, obstacles',
  'src.page.geo.body':
    'Aerodromes, runways, frequencies, navaids, reporting points, airspace and obstacles from openAIP – © openAIP contributors, licensed under CC BY-NC 4.0. Maintained by volunteers and not official. Check AIP and NOTAM before the flight.',
  'src.page.terrain.title': 'Terrain',
  'src.page.terrain.body':
    'Elevation model by Mapterhorn, assembled from several open datasets which are listed there individually. Resolution about 50 m. Obstacles in the terrain profile from openAIP.',
  'src.page.map.title': 'Map',
  'src.page.map.body':
    'Map rendering by Protomaps, map data © OpenStreetMap contributors under ODbL. Alternative map styles by OpenFreeMap – © OpenMapTiles, © OpenStreetMap contributors.',
  'src.page.var.title': 'Magnetic variation',
  'src.page.var.body': 'World Magnetic Model (WMM) by NOAA/NCEI and the British Geological Survey. Public domain.',
  'src.page.ac.title': 'Aircraft presets',
  'src.page.ac.body':
    'Starting values from published handbooks and type certificates (POH/AFM/TCDS), cited per preset. No substitute for your aircraft’s own flight manual and weighing report.',
  'src.page.form.title': 'The form',
  'src.page.form.body': '“Flugdurchführungsplan VFR”, LBA form fsm 1/87.',
  'src.page.back': 'Back',

  /* ---------------- navigation / session ---------------- */
  'nav.aria': 'Sections',
  'nav.flights': 'Flights',
  'nav.settings': 'Settings',
  'nav.sources': 'Data sources',
  'nav.more': 'More',
  'nav.signout': 'Sign out',
  'nav.signin': 'Sign in',
  'nav.noaccount': 'Plan without an account',
  'toast.close': 'Close',
  'session.expired': 'Session expired. Nothing is saved to the server any more; your entries stay in this browser.',
  'session.expired.signin': 'Sign in again',

  /* ---------------- aircraft (/aircraft) ---------------- */
  'nav.aircraft': 'Aircraft',

  /* ---------------- typed banners (app/severity.tsx) ---------------- */
  'bn.more': '+{n} more advisories',
  'bn.fuel-short': 'Minimum fuel {need} l exceeds the {have} l on board.',
  'bn.cap-legs': 'Only twelve legs fit the form – {n} more are flown but not entered.',
  'bn.offline': 'Offline – changes are saved as soon as the connection is back.',
};
